/**
 * Lazy loader for the world-countries polygon layer.
 *
 * The first time the Countries layer is toggled on, this fetches the
 * `world-atlas` 1:110m TopoJSON (~100 KB gzipped) from unpkg and converts
 * it to GeoJSON features via `topojson-client`. The result is cached in
 * module scope so subsequent toggles / re-mounts are free.
 *
 * Each feature carries:
 *   - `id`:          ISO-3166-1 numeric code (string from TopoJSON)
 *   - `properties.name`: English country name
 *   - `geometry`:    Polygon | MultiPolygon
 *
 * The globe view uses `id` → alpha-2 via `ISO_NUMERIC_TO_ALPHA2` to open
 * the region deep-dive modal on click.
 */

import { feature } from "topojson-client"
import type { Objects, Topology } from "topojson-specification"
import type { Feature, MultiPolygon, Polygon } from "geojson"

// `react-globe.gl`'s polygonGeoJsonGeometry accepts `GeoJsonGeometry`,
// so Polygon | MultiPolygon is the shape we hand back.
export interface CountryFeature
  extends Feature<Polygon | MultiPolygon, { name: string }> {
  id: string | number
}

const TOPOJSON_URL = "https://unpkg.com/world-atlas@2/countries-110m.json"

let cached: CountryFeature[] | null = null
let inflight: Promise<CountryFeature[]> | null = null

export async function loadCountries(): Promise<CountryFeature[]> {
  if (cached) return cached
  if (inflight) return inflight

  inflight = (async () => {
    const res = await fetch(TOPOJSON_URL)
    if (!res.ok) throw new Error(`countries-110m fetch ${res.status}`)
    const topo = (await res.json()) as Topology<Objects>

    const objectKey = pickObjectKey(topo)
    // topojson-client's `feature(topology, object)` returns a FeatureCollection
    // when the object is a GeometryCollection (the case for countries-110m).
    const fc = feature(topo, topo.objects[objectKey]!) as unknown as {
      type: "FeatureCollection"
      features: CountryFeature[]
    }

    cached = fc.features
    return cached
  })()

  try {
    return await inflight
  } finally {
    inflight = null
  }
}

function pickObjectKey(topo: Topology<Objects>): string {
  // countries-110m ships an object called `countries`, but fall back to the
  // first object if the upstream bundle ever renames it.
  if (topo.objects["countries"]) return "countries"
  const first = Object.keys(topo.objects)[0]
  if (!first) throw new Error("countries-110m: empty topology")
  return first
}
